<template>
  <div>
    <sdPageHeader title="Nilai Kinerja Organisasi" />
    <Main>
      <a-row :gutter="25">
        <a-col :span="24">
          <sdCards headless>
            <a-row :gutter="16">
              <a-col :span="12">
                <a-select
                  placeholder="Pilih Bulan"
                  style="width: 100%"
                  v-model:value="filters.bulan"
                  @change="refreshData"
                >
                  <a-select-option v-for="bulan in months" :key="bulan.index">
                    {{ bulan.full }}
                  </a-select-option>
                </a-select>
              </a-col>
              <a-col :span="12">
                <a-select
                  placeholder="Pilih Tahun"
                  style="width: 100%"
                  v-model:value="filters.tahun"
                  @change="refreshData"
                >
                  <a-select-option v-for="tahun in tahunOptions" :key="tahun" :value="tahun">
                    {{ tahun }}
                  </a-select-option>
                </a-select>
              </a-col>
            </a-row>
          </sdCards>
        </a-col>
      </a-row>
      <div v-if="isLoading" class="spin">
        <a-spin />
      </div>
      <div v-else>
        <a-row :gutter="25">
          <a-col
            :xxl="6"
            :xl="6"
            :lg="12"
            :md="12"
            :sm="24"
            :xs="24"
            v-for="entitas in dashData.entitas"
            :key="entitas.id"
          >
            <RatioCard>
              <sdCards headless :title="entitas.nama_entitas">
                <div class="ratio-content">
                  <a-row :gutter="0">
                    <a-col :sm="12" :xs="12">
                      <a @click="redirectEntitas(entitas.id)">
                        <sdHeading as="h1" class="medium-number">{{ entitas.nilai }} %</sdHeading>
                      </a>
                    </a-col>
                    <a-col :sm="12" :xs="12">
                      <div :id="'entitas-' + entitas.id" class="gauge-container"></div>
                    </a-col>
                  </a-row>
                </div>
              </sdCards>
            </RatioCard>
          </a-col>
        </a-row>
        <a-row :gutter="25">
          <a-col :span="24">
            <sdCards title="Grafik" bodypadding="0px">
              <div v-if="isLoading" class="spin">
                <a-spin />
              </div>
              <div v-else style="height: 400px;">
                <Line :data="performanceData" :options="chartOptions" />
              </div>
            </sdCards>
          </a-col>
        </a-row>
      </div>
    </Main>
  </div>
</template>

<script>
import { ref, onMounted, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { Main } from '@/view/styled';
import { getItem, setItem } from '@/utility/localStorageControl';
import { RatioCard } from '@/view/dashboard/style';
// Using direct DOM manipulation for gauge charts
let GaugeChart = require('vue-gauge/assets/bundle.js');
import axiosApi from '@/api';

import { Line } from 'vue-chartjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, ChartDataLabels);

export default {
  name: 'NkoDashboard',
  components: {
    Main,
    RatioCard,
    Line,
  },
  setup() {
    const router = useRouter();
    const tahun = getItem('tahun');
    const bulan = getItem('bulan');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const tahunOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const filters = reactive({
      bulan: bulan ? bulan : currentMonth,
      tahun: tahun ? tahun : currentYear,
      title: 'Kontrak Kinerja Organisasi',
    });

    const dashData = reactive({
      entitas: [],
      nilai: 0,
      pencapaian: 0,
      color: null,
      gaugeInstances: {}, // Store gauge instances by ID
      gaugeOptions: {
        needleValue: 0,
        hasNeedle: true,
        needleColor: 'rgba(0, 0, 0, 0.8)',
        arcDelimiters: [30, 50, 70, 95, 99],
        arcColors: ['#f20f0f', '#f74848', '#f76868', '#fa8484', '#eeff59', '#008000'],
        rangeLabel: ['0', '100'],
      },
    });
    const isLoading = ref(false);
    // Initialize with empty data structure
    const performanceData = ref({
      labels: [],
      datasets: [],
    });

    const { months } = require('@/utility/monthsData');

    // Define chart colors for different entities
    const chartColors = {
      borderColors: ['#FFD700', '#00BFFF', '#A9A9A9', '#FF6347', '#32CD32', '#9370DB'],
      backgroundColors: [
        'rgba(255, 215, 0, 0.1)',
        'rgba(0, 191, 255, 0.1)',
        'rgba(169, 169, 169, 0.1)',
        'rgba(255, 99, 71, 0.1)',
        'rgba(50, 205, 50, 0.1)',
        'rgba(147, 112, 219, 0.1)',
      ],
    };

    const chartOptions = ref({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          min: 0,
          max: 110,
          ticks: {
            stepSize: 5,
          },
        },
      },
      plugins: {
        legend: {
          position: 'top',
        },
        datalabels: {
          display: false,
        },
      },
    });

    const contohData = {
      labels: ['Jan', 'Feb', 'Mar'],
      datasets: [
        {
          label: 'Contoh Data',
          data: [10, 20, 15],
          borderColor: '#60a5fa',
          fill: false,
        },
      ],
    };

    const refreshData = () => {
      getData();
    };

    // Fetch data on component mount
    onMounted(() => {
      // Fetch data from API immediately when component mounts
      getData();
    });

    const getData = () => {
      const token = localStorage.getItem('access_token');
      const tahun = filters.tahun;
      const bulan = filters.bulan;

      isLoading.value = true;

      axiosApi
        .get('nko-dashboard', {
          params: {
            year: tahun,
            month: bulan,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(response => {
          // Update companies data from API response
          if (response.data) {
            // Replace the companies array with API data
            dashData.entitas = response.data[0].entitas.map(data => ({
              id: data.id,
              nama_entitas: data.nama_entitas,
              nilai: data.total || 0,
            }));

            // Clear existing gauge instances to prevent duplicates
            dashData.gaugeInstances = {};

            // Process performance data for chart if available
            if (response.data.length > 0 && response.data[0].grafik) {
              const grafikData = response.data[0].grafik;

              // Extract labels (months) from the first entity's data
              const labels = grafikData.labels || [];

              // Create datasets from each entity's performance data
              const datasets = [];

              // Process each entity's data series
              if (grafikData.datasets && Array.isArray(grafikData.datasets)) {
                grafikData.datasets.forEach((dataset, index) => {
                  // Use modulo to cycle through colors if we have more datasets than colors
                  const colorIndex = index % chartColors.borderColors.length;

                  datasets.push({
                    label: dataset.label || `Series ${index + 1}`,
                    data: dataset.data || [],
                    borderColor: chartColors.borderColors[colorIndex],
                    backgroundColor: chartColors.backgroundColors[colorIndex],
                    tension: 0.4,
                  });
                });
              }

              // Update the performance data ref
              performanceData.value = {
                labels: labels,
                datasets: datasets,
              };
            } else {
              // Fallback to empty data if no chart data available
              performanceData.value = {
                labels: [],
                datasets: [],
              };
            }

            // Wait for Vue to update the DOM with new entities
            // Helper function to initialize a single gauge
            const initializeGauge = entitas => {
              const gaugeId = `entitas-${entitas.id}`;
              const element = document.getElementById(gaugeId);

              if (!element) return;

              // Clear the element first to prevent duplicates
              element.innerHTML = '';

              // Create new gauge instance and update needle
              const gauge = GaugeChart.gaugeChart(element, 230, dashData.gaugeOptions);
              dashData.gaugeInstances[gaugeId] = gauge;
              gauge.updateNeedle(entitas.nilai);
            };
            setTimeout(() => {
              // Initialize gauges after DOM update
              dashData.entitas.forEach(initializeGauge);
            }, 500); // Inc
          }
          isLoading.value = false;
        })
        .catch(error => {
          console.error(error);
          isLoading.value = false;
        });
    };

    const redirectEntitas = id => {
      setItem('entitas', id);
      router.push('/nko/entitas');
    };

    return {
      performanceData,
      chartOptions,
      dashData,
      isLoading,
      redirectEntitas,
      refreshData,
      filters,
      tahunOptions,
      months,
      contohData,
      Line,
    };
  },
};
</script>

<style scoped>
.nko-dashboard {
  width: 100%;
}

.gauge-chart-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.gauge-title {
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
}

.gauge-chart {
  width: 100%;
  height: 120px;
  display: flex;
  justify-content: center;
}

.gauge-value {
  font-size: 24px;
  font-weight: bold;
  margin-top: 10px;
}

.gauge-container {
  width: 100%;
  height: 120px;
  display: flex;
  justify-content: right;
  align-items: right;
}

.performance-chart {
  height: 300px;
  width: 100%;
}

.mt-25 {
  margin-top: 25px;
}
</style>
